import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PRODUCT_CATEGORIES, categoryLabel } from "../constants/categories";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import { productsAPI, shopsAPI } from "../services/api";
import { useMembership } from "../context/MembershipContext";
import { useI18n } from "../i18n";
import ProductForm from "../components/ProductForm";
import QuickStockDialog from "../components/QuickStockDialog";
import CatalogPicker from "../components/CatalogPicker";
import CopyFromShop from "../components/CopyFromShop";
import DoseSetup from "../components/DoseSetup";
import { formatMoney } from "../pos/utils/money";

// Juice has no `subcategory` column value — fruit/gourmand/salt lives in
// juice_details.juice_type instead — but it's the same "narrow within this
// category" idea from an owner's point of view, so it gets its own filter
// alongside subcategory rather than being left out because the column differs.
const JUICE_TYPES = ["fruit", "gourmand", "salt"];

const Products = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [currency, setCurrency] = useState("TND");
  const [notice, setNotice] = useState("");
  const { can, flag } = useMembership();
  const catalogOn = flag("starter_catalog");
  // The owner's other shops to copy products from (multi-shop owners only)
  const [copySources, setCopySources] = useState([]);
  const [showCopy, setShowCopy] = useState(false);
  const [showDoses, setShowDoses] = useState(false);
  const dosesOn = flag("dose_sales") && can("products.edit");
  const copyOn = flag("multi_shop") && can("products.edit") && copySources.length > 0;

  useEffect(() => {
    if (!flag("multi_shop") || !can("products.edit")) return;
    shopsAPI
      .copySources()
      .then(({ data }) => setCopySources(Array.isArray(data) ? data : []))
      .catch(() => setCopySources([]));
  }, [flag, can]);
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    subcategory: "",
    juiceType: "",
    brand: "",
    search: "",
  });

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, showArchived]);

  // Open form when navigating to /products/new
  useEffect(() => {
    if (location.pathname === "/admin/products/new") {
      setShowForm(true);
      // Reset URL to /admin/products without refreshing
      navigate("/admin/products", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    shopsAPI
      .current()
      .then(({ data }) => data.shop?.currency && setCurrency(data.shop.currency))
      .catch(() => {});
  }, []);

  const fetchProducts = async () => {
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (showArchived) params.archived = true;

      const response = await productsAPI.getAll(params);
      setProducts(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t("products.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const failure = (err, fallbackKey) =>
    err.response?.data?.message ||
    err.response?.data?.error ||
    t(fallbackKey);

  /**
   * Deleting a product that has been sold would break its history, so the
   * server archives it instead. Ask first what will happen and say so, rather
   * than letting the owner discover it afterwards.
   */
  const handleDelete = async (product) => {
    setNotice("");

    try {
      const { data: impact } = await productsAPI.getDeleteImpact(product.id);

      if (impact.packs.length > 0) {
        alert(
          t("products.deleteInPack", {
            name: product.name,
            packs: impact.packs.map((pack) => pack.name).join(", "),
          }),
        );
        return;
      }

      const message = impact.will_archive
        ? t("products.archiveConfirm", {
            name: product.name,
            sales: impact.sales,
            preorders: impact.preorders,
            deliveries: impact.deliveries,
          })
        : t("products.deleteConfirm");

      if (!window.confirm(message)) return;

      const { data } = await productsAPI.delete(product.id);
      setProducts((current) => current.filter((p) => p.id !== product.id));
      setNotice(
        data.archived
          ? t("products.archivedNotice", { name: product.name })
          : t("products.deletedNotice", { name: product.name }),
      );
    } catch (err) {
      alert(failure(err, "products.deleteFailed"));
    }
  };

  const handleRestore = async (product) => {
    try {
      await productsAPI.restore(product.id);
      setProducts((current) => current.filter((p) => p.id !== product.id));
      setNotice(t("products.restoredNotice", { name: product.name }));
    } catch (err) {
      alert(failure(err, "products.restoreFailed"));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
    fetchProducts();
  };

  // Subcategory and brand narrow further within whatever the server already
  // sent for the selected category — a shop's catalog is small enough that a
  // second round trip isn't worth it, same as the search box already does.
  const subcategoryOptions = useMemo(() => {
    if (!hasSubcategories(filters.category)) return [];

    const present = new Set(
      products.map((product) => product.subcategory).filter(Boolean),
    );

    return subcategoriesFor(filters.category).filter(({ value }) =>
      present.has(value),
    );
  }, [products, filters.category]);

  const juiceTypeOptions = useMemo(() => {
    if (filters.category !== "juice") return [];

    const present = new Set(
      products
        .map((product) => product.juice_details?.juice_type)
        .filter(Boolean),
    );

    return JUICE_TYPES.filter((type) => present.has(type));
  }, [products, filters.category]);

  const brandOptions = useMemo(() => {
    let relevant = products;

    if (filters.subcategory) {
      relevant = relevant.filter(
        (product) => product.subcategory === filters.subcategory,
      );
    }

    if (filters.juiceType) {
      relevant = relevant.filter(
        (product) => product.juice_details?.juice_type === filters.juiceType,
      );
    }

    return [...new Set(relevant.map((product) => product.brand).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );
  }, [products, filters.subcategory, filters.juiceType]);

  const filteredProducts = products.filter(
    (product) =>
      `${product.name} ${product.variant_label || ""}`
        .toLowerCase()
        .includes(filters.search.toLowerCase()) &&
      (!filters.subcategory || product.subcategory === filters.subcategory) &&
      (!filters.juiceType ||
        product.juice_details?.juice_type === filters.juiceType) &&
      (!filters.brand || product.brand === filters.brand),
  );

  const hasActiveFilters = Boolean(
    filters.search ||
      filters.category ||
      filters.subcategory ||
      filters.juiceType ||
      filters.brand,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              {t("products.title")}
            </h1>
            <p className="text-gray-600 mt-1">{t("products.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* The archive is where deletions can be undone */}
            {can("products.delete") && (
              <button
                onClick={() => {
                  setNotice("");
                  setShowArchived(!showArchived);
                }}
                className="px-5 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 text-gray-700 transition-all"
              >
                {showArchived
                  ? t("products.backToProducts")
                  : t("products.archivedView")}
              </button>
            )}
            {!showArchived && can("products.edit") && catalogOn && (
              <button
                onClick={() => setShowCatalog(true)}
                className="px-5 py-3 rounded-lg font-semibold border border-primary-600 text-primary-700 hover:bg-primary-50 transition-all"
              >
                📚 {t("catalog.open")}
              </button>
            )}
            {!showArchived && dosesOn && (
              <button
                onClick={() => setShowDoses(true)}
                className="px-5 py-3 rounded-lg font-semibold border border-primary-600 text-primary-700 hover:bg-primary-50 transition-all"
              >
                💧 {t("doses.setup.button")}
              </button>
            )}
            {!showArchived && copyOn && (
              <button
                onClick={() => setShowCopy(true)}
                className="px-5 py-3 rounded-lg font-semibold border border-primary-600 text-primary-700 hover:bg-primary-50 transition-all"
              >
                🏪 {t("shops.copy.button")}
              </button>
            )}
            {!showArchived && can("products.edit") && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-lg font-semibold shadow-lg transition-all"
              >
                <span className="text-xl me-2 font-bold text-white">+</span>
                {t("products.add")}
              </button>
            )}
          </div>
        </div>

        {showArchived && (
          <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            {t("products.archivedHint")}
          </p>
        )}

        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder={t("products.search")}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <select
            value={filters.category}
            onChange={(e) =>
              setFilters({
                ...filters,
                category: e.target.value,
                subcategory: "",
                juiceType: "",
                brand: "",
              })
            }
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t("products.allCategories")}</option>
            {PRODUCT_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {categoryLabel(category.value)}
              </option>
            ))}
          </select>

          {subcategoryOptions.length > 0 && (
            <select
              value={filters.subcategory}
              onChange={(e) =>
                setFilters({ ...filters, subcategory: e.target.value, brand: "" })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t("products.allSubcategories")}</option>
              {subcategoryOptions.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}

          {juiceTypeOptions.length > 0 && (
            <select
              value={filters.juiceType}
              onChange={(e) =>
                setFilters({ ...filters, juiceType: e.target.value, brand: "" })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t("products.allJuiceTypes")}</option>
              {juiceTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {t(`products.form.juiceTypes.${type}`)}
                </option>
              ))}
            </select>
          )}

          {brandOptions.length > 1 && (
            <select
              value={filters.brand}
              onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t("products.allBrands")}</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{notice}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all flex flex-col"
          >
            <div className="h-48 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
              {product.images && product.images.length > 0 ? (
                <img
                  src={`${
                    process.env.REACT_APP_API_URL || "http://localhost:3010"
                  }${product.images[0]}`}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-6xl">
                  {product.category === "juice"
                    ? "🧪"
                    : product.category === "device"
                      ? "📱"
                      : product.category === "coil"
                        ? "🔌"
                        : "📦"}
                </span>
              )}
            </div>

            {/* The actions sit at the bottom whatever the card above holds,
                so a row of cards lines up */}
            <div className="p-4 flex flex-col flex-1">
              {product.is_draft && (
                <span
                  title={t("products.draftHint")}
                  className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded mb-2 me-2"
                >
                  {t("products.draft")}
                </span>
              )}
              {product.is_new && (
                <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded mb-2">
                  {t("products.new")}
                </span>
              )}
              <h3 className="font-bold text-lg text-gray-800 mb-1 truncate">
                {product.name}
              </h3>
              <p className="text-sm text-gray-600 mb-2">{product.brand}</p>

              <div className="flex items-center justify-between gap-2 mb-3">
                {product.is_draft ? (
                  <span className="text-sm font-semibold text-amber-700">
                    {t("products.noPrice")}
                  </span>
                ) : (
                  <span className="text-xl font-bold text-primary-600 whitespace-nowrap">
                    {formatMoney(product.price)}
                  </span>
                )}
                <span
                  className={`px-2 py-1 rounded text-sm font-medium whitespace-nowrap ${
                    !product.low_stock_muted &&
                    product.stock_quantity <= (product.low_stock_limit ?? 10)
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  {t("products.stock", { count: product.total_stock ?? product.stock_quantity })}
                </span>
              </div>
              {product.warehouse_quantity > 0 && (
                <p className="-mt-2 mb-3 text-xs text-gray-500 text-end">
                  {t("products.stockSplit", {
                    shop: product.stock_quantity,
                    warehouse: product.warehouse_quantity,
                  })}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {categoryLabel(product.category)}
                </span>
                {product.variant_label && (
                  <span className="inline-block px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full">
                    🗂️ {product.variant_label}
                  </span>
                )}
              </div>

              {product.is_draft && !showArchived && (
                <p className="text-xs text-amber-700 mb-3">
                  {t("products.draftHint")}
                </p>
              )}

              {showArchived ? (
                <button
                  onClick={() => handleRestore(product)}
                  className="mt-auto w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 py-2 rounded-lg font-medium transition-all"
                >
                  {t("products.restore")}
                </button>
              ) : (
                <div className="space-y-2 mt-auto">
                  {can("products.stock") && (
                    <button
                      onClick={() => setStockProduct(product)}
                      className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 py-2 rounded-lg font-semibold transition-all"
                    >
                      {t("products.addStock")}
                    </button>
                  )}
                  <div className="flex gap-2">
                    {can("products.edit") && (
                      <button
                        onClick={() => handleEdit(product)}
                        className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded-lg font-medium transition-all"
                      >
                        {t("products.edit")}
                      </button>
                    )}
                    {can("products.delete") && (
                      <button
                        onClick={() => handleDelete(product)}
                        className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded-lg font-medium transition-all"
                      >
                        {t("products.delete")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {t("products.noneFound")}
          </h3>
          <p className="text-gray-600 mb-6">
            {showArchived
              ? t("products.archivedEmpty")
              : hasActiveFilters
                ? t("products.adjustFilters")
                : t("products.startAdding")}
          </p>
          {!hasActiveFilters && !showArchived && can("products.edit") && (
            <div className="flex flex-wrap justify-center gap-3">
              {/* The fastest way to a full shop: copy, don't type */}
              {catalogOn && (
                <button
                  onClick={() => setShowCatalog(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  📚 {t("catalog.open")}
                </button>
              )}
              {copyOn && (
                <button
                  onClick={() => setShowCopy(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  🏪 {t("shops.copy.button")}
                </button>
              )}
              <button
                onClick={() => setShowForm(true)}
                className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-lg font-semibold"
              >
                <span className="text-xl me-2 font-bold">+</span>
                {t("products.addFirst")}
              </button>
            </div>
          )}
        </div>
      )}

      {showCatalog && catalogOn && (
        <CatalogPicker
          currency={currency}
          onClose={() => setShowCatalog(false)}
          onDone={() => fetchProducts()}
        />
      )}

      {showCopy && copyOn && (
        <CopyFromShop
          sources={copySources}
          currency={currency}
          onClose={() => setShowCopy(false)}
          onDone={() => fetchProducts()}
        />
      )}

      {showDoses && dosesOn && (
        <DoseSetup currency={currency} onClose={() => setShowDoses(false)} onDone={() => fetchProducts()} />
      )}

      {showForm && (
        <ProductForm
          product={editingProduct}
          onClose={handleFormClose}
          onConverted={(result, count) => {
            setNotice(t("products.convert.done", { name: result.group.name, count }));
            handleFormClose();
          }}
        />
      )}

      {stockProduct && (
        <QuickStockDialog
          product={stockProduct}
          onClose={() => setStockProduct(null)}
          onSaved={(updated) => {
            setProducts((current) =>
              current.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
            );
            setNotice(
              t("products.stockAdded", {
                name: updated.name,
                count: updated.stock_quantity,
              }),
            );
            setStockProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default Products;
